'use client';

import { c as _c } from "react/compiler-runtime";
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCollectionRouteNavigation } from '@/components/collection/collection-route-navigation';
import { DEFAULT_COLLECTION_SORT } from '@/lib/collection-sort';
import { toCanonicalPath } from '@/lib/markets';

// ── Sort Options ───────────────────────────────────────

const SORT_OPTIONS = [{
  value: DEFAULT_COLLECTION_SORT,
  label: 'Featured'
}, {
  value: 'best-selling',
  label: 'Best Selling'
}, {
  value: 'price-asc',
  label: 'Price: Low to High'
}, {
  value: 'price-desc',
  label: 'Price: High to Low'
}, {
  value: 'newest',
  label: 'Newest'
}] as const;

// ── Filter Types ───────────────────────────────────────

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}
interface FilterSection {
  id: string;
  label: string;
  type: 'checkbox' | 'price-range' | 'availability';
  options?: FilterOption[];
  min?: number;
  max?: number;
}
const DEFAULT_FILTER_SECTIONS: FilterSection[] = [{
  id: 'trades',
  label: 'Trades',
  type: 'checkbox',
  options: []
}, {
  id: 'product_type',
  label: 'Product Type',
  type: 'checkbox',
  options: []
}, {
  id: 'price',
  label: 'Price',
  type: 'price-range',
  min: 0,
  max: 500
}, {
  id: 'availability',
  label: 'Availability',
  type: 'availability',
  options: [{
    value: 'in-stock',
    label: 'In Stock'
  }, {
    value: 'out-of-stock',
    label: 'Out of Stock'
  }]
}];

// ── Shared: Checkbox ───────────────────────────────────

function Checkbox(t0) {
  const $ = _c(5);
  const {
    checked
  } = t0;
  const t1 = `flex h-[18px] w-[18px] shrink-0 items-center justify-center border transition-colors ${checked ? "border-neutral-900 bg-neutral-900" : "border-neutral-300 bg-white"}`;
  let t2;
  if ($[0] !== checked) {
    t2 = checked && <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><polyline points="2 6 5 9 10 3" /></svg>;
    $[0] = checked;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

// ── Shared: Radio ──────────────────────────────────────

function Radio(t0) {
  const $ = _c(5);
  const {
    checked
  } = t0;
  const t1 = `flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 transition-colors ${checked ? "border-neutral-900" : "border-neutral-300"}`;
  let t2;
  if ($[0] !== checked) {
    t2 = checked && <span className="h-2.5 w-2.5 rounded-full bg-neutral-900" />;
    $[0] = checked;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

// ── Accordion ──────────────────────────────────────────

function FilterAccordion(t0) {
  const $ = _c(18);
  const {
    title,
    children,
    defaultOpen: t1
  } = t0;
  const defaultOpen = t1 === undefined ? false : t1;
  const [open, setOpen] = useState(defaultOpen);
  let t2;
  if ($[0] !== open) {
    t2 = () => setOpen(!open);
    $[0] = open;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-900">{title}</span>;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `h-4 w-4 text-neutral-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`;
  let t5;
  if ($[4] !== t4) {
    t5 = <ChevronDown className={t4} />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t5) {
    t6 = <button type="button" onClick={t2} className="flex w-full items-center justify-between py-4 text-left">{t3}{t5}</button>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = `grid transition-[grid-template-rows] duration-200 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`;
  let t8;
  if ($[10] !== children) {
    t8 = <div className="overflow-hidden"><div className="pb-4">{children}</div></div>;
    $[10] = children;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <div className={t7}>{t8}</div>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t6 || $[16] !== t9) {
    t10 = <div className="border-b border-neutral-200 last:border-b-0">{t6}{t9}</div>;
    $[15] = t6;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}

// ── Price Range ────────────────────────────────────────

function PriceRangeFilter(t0) {
  const $ = _c(50);
  const {
    min,
    max,
    currentMin,
    currentMax,
    onChange
  } = t0;
  const range = max - min || 1;
  const leftPercent = (currentMin - min) / range * 100;
  const rightPercent = (max - currentMax) / range * 100;
  const trackRef = useRef(null);
  const t1 = `${leftPercent}%`;
  const t2 = `${rightPercent}%`;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <div ref={trackRef} className="absolute inset-x-0 h-[3px] bg-neutral-200"><div className="absolute h-full bg-neutral-900" style={{
        left: t1,
        right: t2
      }} /></div>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== currentMax || $[4] !== onChange) {
    t4 = e => {
      const v = Number(e.target.value);
      if (v <= currentMax) {
        onChange(v, currentMax);
      }
    };
    $[3] = currentMax;
    $[4] = onChange;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== currentMin || $[7] !== max || $[8] !== min || $[9] !== t4) {
    t5 = <input type="range" min={min} max={max} value={currentMin} onChange={t4} className="pointer-events-none absolute inset-x-0 m-0 h-4 w-full cursor-pointer appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-neutral-900 [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-neutral-900" />;
    $[6] = currentMin;
    $[7] = max;
    $[8] = min;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== currentMin || $[12] !== onChange) {
    t6 = e_0 => {
      const v_0 = Number(e_0.target.value);
      if (v_0 >= currentMin) {
        onChange(currentMin, v_0);
      }
    };
    $[11] = currentMin;
    $[12] = onChange;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== currentMax || $[15] !== max || $[16] !== min || $[17] !== t6) {
    t7 = <input type="range" min={min} max={max} value={currentMax} onChange={t6} className="pointer-events-none absolute inset-x-0 m-0 h-4 w-full cursor-pointer appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-neutral-900 [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-neutral-900" />;
    $[14] = currentMax;
    $[15] = max;
    $[16] = min;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t3 || $[20] !== t5 || $[21] !== t7) {
    t8 = <div className="relative mx-2 h-4 flex items-center">{t3}{t5}{t7}</div>;
    $[19] = t3;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="text-xs text-neutral-400">$</span>;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== currentMax || $[25] !== min || $[26] !== onChange) {
    t10 = e_1 => {
      const v_1 = Math.max(min, Math.min(Number(e_1.target.value), currentMax));
      onChange(v_1, currentMax);
    };
    $[24] = currentMax;
    $[25] = min;
    $[26] = onChange;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== currentMax || $[29] !== currentMin || $[30] !== min || $[31] !== t10) {
    t11 = <div className="flex flex-1 items-center border border-neutral-200 px-2 py-1.5">{t9}<input type="number" min={min} max={currentMax} value={currentMin} onChange={t10} className="w-full bg-transparent pl-1 text-xs text-neutral-900 outline-none" /></div>;
    $[28] = currentMax;
    $[29] = currentMin;
    $[30] = min;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="text-[10px] text-neutral-300">-</span>;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="text-xs text-neutral-400">$</span>;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== currentMin || $[36] !== max || $[37] !== onChange) {
    t14 = e_2 => {
      const v_2 = Math.max(currentMin, Math.min(Number(e_2.target.value), max));
      onChange(currentMin, v_2);
    };
    $[35] = currentMin;
    $[36] = max;
    $[37] = onChange;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== currentMax || $[40] !== currentMin || $[41] !== max || $[42] !== t14) {
    t15 = <div className="flex flex-1 items-center border border-neutral-200 px-2 py-1.5">{t13}<input type="number" min={currentMin} max={max} value={currentMax} onChange={t14} className="w-full bg-transparent pl-1 text-xs text-neutral-900 outline-none" /></div>;
    $[39] = currentMax;
    $[40] = currentMin;
    $[41] = max;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t11 || $[45] !== t15) {
    t16 = <div className="flex items-center gap-2">{t11}{t12}{t15}</div>;
    $[44] = t11;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t16 || $[48] !== t8) {
    t17 = <div className="space-y-3">{t8}{t16}</div>;
    $[47] = t16;
    $[48] = t8;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  return t17;
}

// ── Checkbox List ──────────────────────────────────────

function CheckboxFilterList(t0) {
  const $ = _c(20);
  const {
    section,
    pendingFilters,
    onToggleFilter
  } = t0;
  const [showAll, setShowAll] = useState(false);
  let t1;
  if ($[0] !== section.options) {
    t1 = section.options || [];
    $[0] = section.options;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let t2;
  if ($[2] !== options || $[3] !== showAll) {
    t2 = showAll ? options : options.slice(0, 5);
    $[2] = options;
    $[3] = showAll;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const visibleOptions = t2;
  let t3;
  if ($[5] !== onToggleFilter || $[6] !== pendingFilters || $[7] !== section.id || $[8] !== visibleOptions) {
    let t4;
    if ($[10] !== onToggleFilter || $[11] !== pendingFilters || $[12] !== section.id) {
      t4 = opt => {
        const selected = (pendingFilters[section.id] || []).includes(opt.value);
        return <label key={opt.value} className="flex cursor-pointer items-center gap-2.5 py-1 transition-colors hover:text-neutral-900"><input type="checkbox" checked={selected} onChange={() => onToggleFilter(section.id, opt.value)} className="sr-only" /><Checkbox checked={selected} /><span className="text-xs uppercase tracking-wide text-neutral-600">{opt.label}</span>{opt.count !== undefined && <span className="ml-auto text-[10px] text-neutral-400">({opt.count})</span>}</label>;
      };
      $[10] = onToggleFilter;
      $[11] = pendingFilters;
      $[12] = section.id;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = visibleOptions.map(t4);
    $[5] = onToggleFilter;
    $[6] = pendingFilters;
    $[7] = section.id;
    $[8] = visibleOptions;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[14] !== options.length || $[15] !== showAll) {
    t4 = options.length > 5 && <button type="button" onClick={() => setShowAll(!showAll)} className="mt-1 text-left text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-900 transition-colors hover:text-neutral-600">{showAll ? "Show Less" : "Show More"}</button>;
    $[14] = options.length;
    $[15] = showAll;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t3 || $[18] !== t4) {
    t5 = <div className="flex flex-col gap-1">{t3}{t4}</div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}

// ── Availability ───────────────────────────────────────

function AvailabilityFilter(t0) {
  const $ = _c(33);
  const {
    pendingFilters,
    onToggleFilter
  } = t0;
  let t1;
  if ($[0] !== pendingFilters.availability) {
    t1 = pendingFilters.availability || [];
    $[0] = pendingFilters.availability;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selected = t1;
  let t2;
  if ($[2] !== selected) {
    t2 = selected.includes("in-stock");
    $[2] = selected;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const inStock = t2;
  let t3;
  if ($[4] !== selected) {
    t3 = selected.includes("out-of-stock");
    $[4] = selected;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const outOfStock = t3;
  let t4;
  if ($[6] !== onToggleFilter) {
    t4 = () => onToggleFilter("availability", "in-stock");
    $[6] = onToggleFilter;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== inStock || $[9] !== t4) {
    t5 = <input type="checkbox" checked={inStock} onChange={t4} className="sr-only" />;
    $[8] = inStock;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== inStock) {
    t6 = <Checkbox checked={inStock} />;
    $[11] = inStock;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-xs uppercase tracking-wide text-neutral-600">In Stock</span>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t6) {
    t8 = <label className="flex cursor-pointer items-center gap-2.5 py-1">{t5}{t6}{t7}</label>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== onToggleFilter) {
    t9 = () => onToggleFilter("availability", "out-of-stock");
    $[17] = onToggleFilter;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== outOfStock || $[20] !== t9) {
    t10 = <input type="checkbox" checked={outOfStock} onChange={t9} className="sr-only" />;
    $[19] = outOfStock;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== outOfStock) {
    t11 = <Checkbox checked={outOfStock} />;
    $[22] = outOfStock;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = `text-xs uppercase tracking-wide ${outOfStock ? "text-neutral-600" : "text-neutral-400 line-through"}`;
  let t13;
  if ($[24] !== t12) {
    t13 = <span className={t12}>Out of Stock</span>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t13) {
    t14 = <label className="flex cursor-pointer items-center gap-2.5 py-1">{t10}{t11}{t13}</label>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14 || $[31] !== t8) {
    t15 = <div className="flex flex-col gap-1">{t8}{t14}</div>;
    $[30] = t14;
    $[31] = t8;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}

// ── Filter Content ─────────────────────────────────────

function FilterContent(t0) {
  const $ = _c(23);
  const {
    pendingSort,
    setPendingSort,
    pendingFilters,
    pendingPriceRange,
    onToggleFilter,
    onPriceRangeChange,
    onClearAll,
    sections,
    showHeader: t1
  } = t0;
  const showHeader = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== onClearAll || $[1] !== showHeader) {
    t2 = showHeader && <div className="flex items-center justify-between pb-1"><h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-900">{"Filter & Sort"}</h2><button type="button" onClick={onClearAll} className="text-[10px] uppercase tracking-wide text-neutral-400 transition-colors hover:text-neutral-900">Clear All</button></div>;
    $[0] = onClearAll;
    $[1] = showHeader;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== pendingSort || $[4] !== setPendingSort) {
    t3 = SORT_OPTIONS.map(opt => <label key={opt.value} className="flex cursor-pointer items-center gap-2.5 py-1"><input type="radio" name="sort" value={opt.value} checked={pendingSort === opt.value} onChange={() => setPendingSort(opt.value)} className="sr-only" /><Radio checked={pendingSort === opt.value} /><span className="text-xs text-neutral-600">{opt.label}</span></label>);
    $[3] = pendingSort;
    $[4] = setPendingSort;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <FilterAccordion title="Sort By" defaultOpen={true}><div className="flex flex-col gap-1">{t3}</div></FilterAccordion>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onPriceRangeChange || $[9] !== onToggleFilter || $[10] !== pendingFilters || $[11] !== pendingPriceRange || $[12] !== sections) {
    let t6;
    if ($[14] !== onPriceRangeChange || $[15] !== onToggleFilter || $[16] !== pendingFilters || $[17] !== pendingPriceRange) {
      t6 = section => {
        if (section.type === "checkbox") {
          if (!section.options || section.options.length === 0) {
            return null;
          }
          return <FilterAccordion key={section.id} title={section.label}><CheckboxFilterList section={section} pendingFilters={pendingFilters} onToggleFilter={onToggleFilter} /></FilterAccordion>;
        }
        if (section.type === "price-range") {
          return <FilterAccordion key={section.id} title={section.label}><PriceRangeFilter min={section.min ?? 0} max={section.max ?? 500} currentMin={pendingPriceRange.min} currentMax={pendingPriceRange.max} onChange={onPriceRangeChange} /></FilterAccordion>;
        }
        if (section.type === "availability") {
          return <FilterAccordion key={section.id} title={section.label}><AvailabilityFilter pendingFilters={pendingFilters} onToggleFilter={onToggleFilter} /></FilterAccordion>;
        }
        return null;
      };
      $[14] = onPriceRangeChange;
      $[15] = onToggleFilter;
      $[16] = pendingFilters;
      $[17] = pendingPriceRange;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    t5 = sections.map(t6);
    $[8] = onPriceRangeChange;
    $[9] = onToggleFilter;
    $[10] = pendingFilters;
    $[11] = pendingPriceRange;
    $[12] = sections;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[19] !== t2 || $[20] !== t4 || $[21] !== t5) {
    t6 = <>{t2}{t4}{t5}</>;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
}

// ── Helpers ────────────────────────────────────────────

function getDefaultPriceRange(sections: FilterSection[]) {
  const priceSection = sections.find(s => s.type === 'price-range');
  return {
    min: priceSection?.min ?? 0,
    max: priceSection?.max ?? 500
  };
}
function parsePriceRangeFromParams(searchParams: URLSearchParams, sections: FilterSection[]) {
  const defaults = getDefaultPriceRange(sections);
  const priceMin = searchParams.get('filter.price.min');
  const priceMax = searchParams.get('filter.price.max');
  return {
    min: priceMin ? Number(priceMin) : defaults.min,
    max: priceMax ? Number(priceMax) : defaults.max
  };
}
function buildFilterParams(pendingSort: string, pendingFilters: Record<string, string[]>, pendingPriceRange: {
  min: number;
  max: number;
}, defaultPriceRange: {
  min: number;
  max: number;
}) {
  const params = new URLSearchParams();
  if (pendingSort && pendingSort !== DEFAULT_COLLECTION_SORT) {
    params.set('sort', pendingSort);
  }
  for (const [key, values] of Object.entries(pendingFilters)) {
    if (values.length > 0) {
      params.set(`filter.${key}`, values.join(','));
    }
  }
  if (pendingPriceRange.min !== defaultPriceRange.min) {
    params.set('filter.price.min', String(pendingPriceRange.min));
  }
  if (pendingPriceRange.max !== defaultPriceRange.max) {
    params.set('filter.price.max', String(pendingPriceRange.max));
  }
  params.delete('after');
  return params;
}

// ── Desktop Sidebar ────────────────────────────────────

interface FilterSidebarProps {
  filterSections?: FilterSection[];
}
export function FilterSidebar(t0) {
  const $ = _c(46);
  const {
    filterSections
  } = t0;
  const router = useRouter();
  const routeNavigation = useCollectionRouteNavigation();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("sort") || DEFAULT_COLLECTION_SORT;
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentSort = t1;
  const sections = filterSections?.length ? filterSections : DEFAULT_FILTER_SECTIONS;
  let t2;
  if ($[2] !== sections) {
    t2 = getDefaultPriceRange(sections);
    $[2] = sections;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const defaultPriceRange = t2;
  const [pendingSort, setPendingSort] = useState(currentSort);
  let t3;
  if ($[4] !== searchParams || $[5] !== sections) {
    t3 = () => {
      const initial = {};
      for (const section of sections) {
        if (section.type === "price-range") {
          continue;
        }
        const param = searchParams.get(`filter.${section.id}`);
        if (param) {
          initial[section.id] = param.split(",");
        }
      }
      return initial;
    };
    $[4] = searchParams;
    $[5] = sections;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [pendingFilters, setPendingFilters] = useState(t3);
  let t4;
  if ($[7] !== searchParams || $[8] !== sections) {
    t4 = () => parsePriceRangeFromParams(searchParams, sections);
    $[7] = searchParams;
    $[8] = sections;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const [pendingPriceRange, setPendingPriceRange] = useState(t4);
  let t5;
  if ($[10] !== defaultPriceRange || $[11] !== pathname || $[12] !== pendingFilters || $[13] !== pendingPriceRange || $[14] !== pendingSort || $[15] !== routeNavigation || $[16] !== router) {
    t5 = () => {
      const params = buildFilterParams(pendingSort, pendingFilters, pendingPriceRange, defaultPriceRange);
      const qs = params.toString();
      const canonicalPathname = toCanonicalPath(pathname);
      const href = qs ? `${canonicalPathname}?${qs}` : canonicalPathname;
      if (routeNavigation) {
        routeNavigation.navigate(href);
      } else {
        router.push(href);
      }
    };
    $[10] = defaultPriceRange;
    $[11] = pathname;
    $[12] = pendingFilters;
    $[13] = pendingPriceRange;
    $[14] = pendingSort;
    $[15] = routeNavigation;
    $[16] = router;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const applyFilters = t5;
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (sectionId, value) => {
      setPendingFilters(prev => {
        const current = prev[sectionId] || [];
        const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        return {
          ...prev,
          [sectionId]: next
        };
      });
    };
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const handleToggleFilter = t6;
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = value_0 => {
      setPendingSort(value_0);
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleSortChange = t7;
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (min, max) => {
      setPendingPriceRange({
        min,
        max
      });
    };
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handlePriceRangeChange = t8;
  let t9;
  if ($[21] !== defaultPriceRange) {
    t9 = () => {
      setPendingSort(DEFAULT_COLLECTION_SORT);
      setPendingFilters({});
      setPendingPriceRange(defaultPriceRange);
    };
    $[21] = defaultPriceRange;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleClearAll = t9;
  let t10;
  if ($[23] !== pendingFilters || $[24] !== pendingPriceRange || $[25] !== pendingSort || $[26] !== searchParams || $[27] !== sections) {
    let t11;
    if ($[29] !== searchParams) {
      t11 = s_0 => {
        const value_1 = searchParams.get(`filter.${s_0.id}`);
        return value_1 ? [[s_0.id, value_1.split(",")]] : [];
      };
      $[29] = searchParams;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    const currentUrlFilters = Object.fromEntries(sections.filter(_temp).flatMap(t11));
    let t12;
    if ($[31] !== searchParams || $[32] !== sections) {
      t12 = parsePriceRangeFromParams(searchParams, sections);
      $[31] = searchParams;
      $[32] = sections;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    const currentUrlPriceRange = t12;
    t10 = pendingSort !== (searchParams.get("sort") || DEFAULT_COLLECTION_SORT) || JSON.stringify(pendingFilters) !== JSON.stringify(currentUrlFilters) || pendingPriceRange.min !== currentUrlPriceRange.min || pendingPriceRange.max !== currentUrlPriceRange.max;
    $[23] = pendingFilters;
    $[24] = pendingPriceRange;
    $[25] = pendingSort;
    $[26] = searchParams;
    $[27] = sections;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const hasChanges = t10;
  let t11;
  if ($[34] !== handleClearAll || $[35] !== pendingFilters || $[36] !== pendingPriceRange || $[37] !== pendingSort || $[38] !== sections) {
    t11 = <FilterContent pendingSort={pendingSort} setPendingSort={handleSortChange} pendingFilters={pendingFilters} pendingPriceRange={pendingPriceRange} onToggleFilter={handleToggleFilter} onPriceRangeChange={handlePriceRangeChange} onClearAll={handleClearAll} sections={sections} />;
    $[34] = handleClearAll;
    $[35] = pendingFilters;
    $[36] = pendingPriceRange;
    $[37] = pendingSort;
    $[38] = sections;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== applyFilters || $[41] !== hasChanges) {
    t12 = hasChanges && <div className="sticky bottom-0 bg-white pb-2 pt-3"><button type="button" onClick={applyFilters} className="w-full rounded-md bg-neutral-900 py-2.5 text-[11px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-neutral-800">Apply Filters</button></div>;
    $[40] = applyFilters;
    $[41] = hasChanges;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t11 || $[44] !== t12) {
    t13 = <aside className="hidden w-56 shrink-0 lg:block"><div className="sticky top-[4.5rem] max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain pr-2 scrollbar-thin">{t11}{t12}</div></aside>;
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
}

// ── Static Fallback (for Suspense) ─────────────────────
//
// Rendered as the Suspense fallback while FilterSidebar (which calls
// useSearchParams — a dynamic API under `cacheComponents: true`) resolves
// at request time. Renders the same <aside> markup as FilterSidebar with
// default state and no-op handlers so the prerendered HTML visually matches
// the hydrated component when no filters are active — no skeleton flash on
// refresh. When filters/sort are active the hydration briefly shows defaults
// before syncing to the URL state, which is still preferable to a skeleton.
function _temp(s) {
  return s.type !== "price-range";
}
const NOOP = () => {};
export function FilterSidebarStaticFallback(t0) {
  const $ = _c(6);
  const {
    filterSections
  } = t0;
  const sections = filterSections?.length ? filterSections : DEFAULT_FILTER_SECTIONS;
  let t1;
  if ($[0] !== sections) {
    t1 = getDefaultPriceRange(sections);
    $[0] = sections;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultPriceRange = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== defaultPriceRange || $[4] !== sections) {
    t3 = <aside className="hidden w-56 shrink-0 lg:block"><div className="sticky top-[4.5rem] max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain pr-2 scrollbar-thin"><FilterContent pendingSort={DEFAULT_COLLECTION_SORT} setPendingSort={NOOP} pendingFilters={t2} pendingPriceRange={defaultPriceRange} onToggleFilter={NOOP} onPriceRangeChange={NOOP} onClearAll={NOOP} sections={sections} /></div></aside>;
    $[3] = defaultPriceRange;
    $[4] = sections;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

// ── Mobile Drawer ──────────────────────────────────────

interface FilterDrawerProps {
  filterSections?: FilterSection[];
}
export function FilterMobileDrawer(t0) {
  const $ = _c(49);
  const {
    filterSections
  } = t0;
  const router = useRouter();
  const routeNavigation = useCollectionRouteNavigation();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("sort") || DEFAULT_COLLECTION_SORT;
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentSort = t1;
  const sections = filterSections?.length ? filterSections : DEFAULT_FILTER_SECTIONS;
  let t2;
  if ($[2] !== sections) {
    t2 = getDefaultPriceRange(sections);
    $[2] = sections;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const defaultPriceRange = t2;
  const [pendingSort, setPendingSort] = useState(currentSort);
  let t3;
  if ($[4] !== searchParams || $[5] !== sections) {
    t3 = () => {
      const initial = {};
      for (const section of sections) {
        if (section.type === "price-range") {
          continue;
        }
        const param = searchParams.get(`filter.${section.id}`);
        if (param) {
          initial[section.id] = param.split(",");
        }
      }
      return initial;
    };
    $[4] = searchParams;
    $[5] = sections;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [pendingFilters, setPendingFilters] = useState(t3);
  let t4;
  if ($[7] !== searchParams || $[8] !== sections) {
    t4 = () => parsePriceRangeFromParams(searchParams, sections);
    $[7] = searchParams;
    $[8] = sections;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const [pendingPriceRange, setPendingPriceRange] = useState(t4);
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  let t5;
  if ($[10] !== pendingFilters) {
    t5 = Object.values(pendingFilters).reduce(_temp2, 0);
    $[10] = pendingFilters;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const activeFilterCount = t5 + (pendingPriceRange.min !== defaultPriceRange.min || pendingPriceRange.max !== defaultPriceRange.max ? 1 : 0);
  let t6;
  let t7;
  if ($[12] !== isOpen) {
    t6 = () => {
      if (isOpen) {
        setMounted(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setVisible(true));
        });
      } else {
        setVisible(false);
        const timer = setTimeout(() => setMounted(false), 300);
        return () => clearTimeout(timer);
      }
    };
    t7 = [isOpen];
    $[12] = isOpen;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== searchParams || $[16] !== sections) {
    t8 = () => {
      setPendingSort(searchParams.get("sort") || DEFAULT_COLLECTION_SORT);
      const initial_0 = {};
      for (const section_0 of sections) {
        if (section_0.type === "price-range") {
          continue;
        }
        const param_0 = searchParams.get(`filter.${section_0.id}`);
        if (param_0) {
          initial_0[section_0.id] = param_0.split(",");
        }
      }
      setPendingFilters(initial_0);
      setPendingPriceRange(parsePriceRangeFromParams(searchParams, sections));
      setIsOpen(true);
    };
    $[15] = searchParams;
    $[16] = sections;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleOpen = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setIsOpen(false);
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleClose = t9;
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = (sectionId, value) => {
      setPendingFilters(prev => {
        const current = prev[sectionId] || [];
        const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        return {
          ...prev,
          [sectionId]: next
        };
      });
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleToggleFilter = t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = (min, max) => {
      setPendingPriceRange({
        min,
        max
      });
    };
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handlePriceRangeChange = t11;
  let t12;
  if ($[21] !== defaultPriceRange || $[22] !== pathname || $[23] !== pendingFilters || $[24] !== pendingPriceRange || $[25] !== pendingSort || $[26] !== routeNavigation || $[27] !== router) {
    t12 = () => {
      const params = buildFilterParams(pendingSort, pendingFilters, pendingPriceRange, defaultPriceRange);
      const qs = params.toString();
      const canonicalPathname = toCanonicalPath(pathname);
      const href = qs ? `${canonicalPathname}?${qs}` : canonicalPathname;
      if (routeNavigation) {
        routeNavigation.navigate(href);
      } else {
        router.push(href);
      }
      setIsOpen(false);
    };
    $[21] = defaultPriceRange;
    $[22] = pathname;
    $[23] = pendingFilters;
    $[24] = pendingPriceRange;
    $[25] = pendingSort;
    $[26] = routeNavigation;
    $[27] = router;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const handleApply = t12;
  let t13;
  if ($[29] !== defaultPriceRange) {
    t13 = () => {
      setPendingSort(DEFAULT_COLLECTION_SORT);
      setPendingFilters({});
      setPendingPriceRange(defaultPriceRange);
    };
    $[29] = defaultPriceRange;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const handleClearAll = t13;
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SlidersHorizontal className="h-4 w-4" />;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = activeFilterCount > 0 ? `Filters (${activeFilterCount})` : "Filter & Sort";
  let t16;
  if ($[32] !== t15) {
    t16 = <span className="text-[11px] font-bold uppercase tracking-[0.15em]">{t15}</span>;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== handleOpen || $[35] !== t16) {
    t17 = <button type="button" onClick={handleOpen} className="flex appearance-none items-center gap-1.5 border-0 bg-transparent p-0 text-neutral-900 lg:hidden">{t14}{t16}</button>;
    $[34] = handleOpen;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== handleApply || $[38] !== handleClearAll || $[39] !== mounted || $[40] !== pendingFilters || $[41] !== pendingPriceRange || $[42] !== pendingSort || $[43] !== sections || $[44] !== visible) {
    t18 = mounted && <div className="fixed inset-0 z-50"><button type="button" aria-label="Close filters" onClick={handleClose} className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`} /><div className={`absolute inset-y-0 left-0 flex w-full max-w-sm flex-col bg-white shadow-2xl transition-transform duration-300 ease-out ${visible ? "translate-x-0" : "-translate-x-full"}`}><div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3"><h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-900">{"Filter & Sort"}</h2><button type="button" onClick={handleClose} className="p-0.5 text-neutral-400 transition-colors hover:text-neutral-900" aria-label="Close"><X className="h-5 w-5" /></button></div><div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3"><FilterContent pendingSort={pendingSort} setPendingSort={setPendingSort} pendingFilters={pendingFilters} pendingPriceRange={pendingPriceRange} onToggleFilter={handleToggleFilter} onPriceRangeChange={handlePriceRangeChange} onClearAll={handleClearAll} sections={sections} showHeader={false} /></div><div className="flex items-center gap-3 border-t border-neutral-200 px-4 py-3"><button type="button" onClick={handleClearAll} className="flex-1 text-center text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-400 transition-colors hover:text-neutral-900">Clear All</button><button type="button" onClick={handleApply} className="flex-1 rounded-md bg-neutral-900 py-3 text-center text-[11px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-neutral-800">Apply</button></div></div></div>;
    $[37] = handleApply;
    $[38] = handleClearAll;
    $[39] = mounted;
    $[40] = pendingFilters;
    $[41] = pendingPriceRange;
    $[42] = pendingSort;
    $[43] = sections;
    $[44] = visible;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t17 || $[47] !== t18) {
    t19 = <>{t17}{t18}</>;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  return t19;
}
function _temp2(sum, arr) {
  return sum + arr.length;
}
export type { FilterOption, FilterSection };